import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead } from "@/components/shared";

const MODELS = [
  {
    name: "Hourly Consultation",
    price: "From $200",
    per: "per hour",
    best: "For advice sessions, design planning, material selection and supplier coordination — ideal for smaller projects or a professional second opinion.",
    features: ["Design planning & direction", "Material & finish selection", "Supplier coordination", "Small-project styling guidance"],
    featured: false,
  },
  {
    name: "Fixed-Fee Projects",
    price: "From $350",
    per: "per project",
    best: "Clear, quoted-in-advance pricing for defined rooms and scopes — you know the fee before we begin.",
    features: ["Colour consultation — from $350", "Furniture & styling packages — from $1,500", "Kitchen & bathroom design — from $2,500", "Full room design — from $3,500"],
    featured: true,
  },
  {
    name: "Whole-Home & Renovations",
    price: "10–15%",
    per: "of project budget",
    best: "For larger renovations and full-home transformations, the design fee scales with the scope and complexity of the project.",
    features: ["Fee aligned to project scale", "Full concept-to-completion oversight", "Trade & supplier coordination", "Luxury projects up to 20%"],
    featured: false,
  },
];

export default function InteriorPricing() {
  return (
    <section className="mt-24" data-testid="interior-pricing">
      <SectionHead
        overline="Design Pricing"
        title="Three honest ways to work together."
        copy="Guide pricing based on professional design-industry benchmarks — every project is confirmed with a written quote after a walk-through."
      />
      <div className="mt-12 grid gap-8 lg:grid-cols-3">
        {MODELS.map((m, i) => (
          <Reveal key={m.name} delay={i * 0.08} className="h-full">
            <div
              data-testid={`interior-model-${m.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`flex h-full flex-col p-8 lg:p-9 ${m.featured ? "bg-navy text-ivory shadow-2xl shadow-navy/20" : "border border-navy/10 bg-white"}`}
            >
              <h3 className={`font-serif text-2xl ${m.featured ? "text-ivory" : "text-navy"}`}>{m.name}</h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className={`font-serif text-4xl font-light ${m.featured ? "text-bermuda" : "text-navy"}`}>{m.price}</span>
                <span className={`text-xs ${m.featured ? "text-ivory/60" : "text-navy/50"}`}>{m.per}</span>
              </div>
              <p className={`mt-4 text-[13px] leading-relaxed ${m.featured ? "text-ivory/70" : "text-navy/60"}`}>{m.best}</p>
              <ul className="mt-6 flex-1 space-y-3">
                {m.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[13px]">
                    <Check size={14} strokeWidth={2.5} className={`mt-0.5 shrink-0 ${m.featured ? "text-bermuda" : "text-ocean"}`} />
                    <span className={m.featured ? "text-ivory/85" : "text-navy/75"}>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-10 flex flex-wrap items-center justify-between gap-6 border-l-2 border-bermuda bg-sand-light p-7">
          <p className="max-w-xl text-sm leading-relaxed text-navy/70">
            Not sure which fits? Most projects begin with a single paid consultation — you'll leave with a plan,
            honest numbers and zero obligation to continue.
          </p>
          <Link
            to="/consultation"
            data-testid="interior-pricing-cta"
            className="group inline-flex items-center gap-2.5 bg-navy px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-bermuda-dark"
          >
            Book a Design Consultation
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>
    </section>
  );
}
